import { createSelector } from 'reselect';
import isEqual from 'lodash/isEqual';
import {
  assetUpdateToHumanReadableRecord,
  capitalize,
  displayDateInProjectTime,
  getAssetChangesKeyedByKey,
  getUniqueKeysFromCollection,
  getLayerPathFromLayerID,
  getMetadata,
  getPlannedAssetComparison,
  getPlannedTwinIDChanges,
  isIsoDate,
  plannedTwinIDToHumanReadableRecord,
} from '../utils';
import {
  getAssetsKeyedByLabel,
  getAssetsKeyedByTwinId,
  getAssetStatusesKeyedById,
  getAssetTypes,
  getProjectName,
  getUserInfo,
} from '.';
import client from '../services/Client';
import map from './map';
import { AssetUpdate, PlanResponseLog } from '../config/types';
import { DynamicPicklistColumn } from '../components/atoms/SegmentedPicklist';

const UPDATE_TOOL_CREATE_TITLE = 'Create';
const UPDATE_TOOL_ARCHIVE_TITLE = 'Delete';
const UPDATE_TOOL_BY_TWIN_ID_TITLE = 'Update by Twin ID';
const UPDATE_TOOL_BY_LABEL_TITLE = 'Update by Label';

export const getAssetChangesKeyedByLabel = createSelector(
  [map.plannedUpdatedAsset, getAssetsKeyedByLabel, map.layersKeyedById],
  (plannedUpdatedAsset, assetsKeyedByLabel, layersKeyedById) => {
    const { byLabel } = plannedUpdatedAsset?.updates || {};
    return getAssetChangesKeyedByKey(byLabel, 'label', assetsKeyedByLabel, layersKeyedById);
  },
);

export const getAssetUpdateToolExcelData = createSelector(
  [
    map.plannedUpdatedAsset,
    map.timezone,
    map.assetFileName,
    map.assetImportTime,
    map.tenant,
    map.layersKeyedById,
    map.assetUpdateMode,
    getAssetsKeyedByLabel,
    getAssetsKeyedByTwinId,
    getProjectName,
    getUserInfo,
  ],
  (
    plannedUpdatedAsset,
    timezone,
    assetFileName,
    assetImportTime,
    tenant,
    layersKeyedById,
    assetUpdateMode,
    assetsKeyedByLabel,
    assetsKeyedByTwinId,
    projectName,
    userInfo,
  ) => {
    if (!plannedUpdatedAsset) return undefined;

    const {
      updates, logging, totalCount, dbAssetTypeCount, unchanged,
    } = plannedUpdatedAsset;
    const keys = [
      'created',
      'archived',
      'byLabel',
      'byTwinID',
      'errors',
      'warnings',
      'info',
      'metadata',
    ];
    const data = { ...updates, ...logging };
    const changed = data.byTwinID.length + data.byLabel.length;

    return keys.map((plannedUpdatedAssetUpdateKey) => {
      const planned = data[plannedUpdatedAssetUpdateKey] || [];

      if (plannedUpdatedAssetUpdateKey === 'created') {
        const flattenPlanned = planned.map((row) => {
          const flattenRow = {
            ...row,
            ...row.fields,
          };
          delete flattenRow.fields;
          return flattenRow;
        });
        return {
          sheet: UPDATE_TOOL_CREATE_TITLE,
          content: flattenPlanned,
        };
      }

      if (plannedUpdatedAssetUpdateKey === 'archived') {
        const flattenPlanned = planned.map((row) => {
          const flattenRow = {
            ...row,
            ...row.fields,
          };
          delete flattenRow.fields;
          return flattenRow;
        });
        return {
          sheet: UPDATE_TOOL_ARCHIVE_TITLE,
          content: flattenPlanned,
        };
      }

      if (
        plannedUpdatedAssetUpdateKey === 'byTwinID'
        || plannedUpdatedAssetUpdateKey === 'byLabel'
      ) {
        const plannedContent = planned?.map((plannedAsset) => {
          const formattedPlannedAsset = { ...plannedAsset, ...plannedAsset?.fields };
          const asset = plannedUpdatedAssetUpdateKey === 'byTwinID'
            ? assetsKeyedByTwinId.get(formattedPlannedAsset.twinID)
            : assetsKeyedByLabel.get(formattedPlannedAsset.label);
          const { update } = getPlannedAssetComparison(formattedPlannedAsset, asset);
          const content = {};

          update.forEach(({ key, currentValue, newValue }) => {
            if (key !== 'fields') {
              content[
                `${key === 'layerID' ? 'Layer' : capitalize(key)} [Current value]`
              ] = key === 'layerID'
                ? `${getLayerPathFromLayerID(
                  layersKeyedById,
                  currentValue,
                )} (${currentValue})` : currentValue;
              content[
                `${key === 'layerID' ? 'Layer' : capitalize(key)} [New value]`
              ] = key === 'layerID'
                ? `${getLayerPathFromLayerID(
                  layersKeyedById,
                  newValue,
                )} (${newValue})` : newValue;
            }
          });
          return content;
        });

        return {
          sheet: plannedUpdatedAssetUpdateKey === 'byTwinID'
            ? UPDATE_TOOL_BY_TWIN_ID_TITLE : UPDATE_TOOL_BY_LABEL_TITLE,
          columns: Object.keys(plannedContent[0] || []).map(key => ({
            label: key,
            value: key,
          })),
          content: plannedContent,
        };
      }

      if (plannedUpdatedAssetUpdateKey === 'metadata') {
        return getMetadata({
          tenant,
          projectName,
          userInfo,
          timezone,
          assetUpdateMode,
          fileName: assetFileName,
          unchanged,
          importTime: assetImportTime,
          changed,
          totalCount,
          totalBefore: dbAssetTypeCount,
        });
      }

      const flattenPlanned = planned.map((row) => {
        const { msg, metadata } = row || {};
        const { description } = metadata || {};
        const flattenRow = {
          Info:
            `${msg} (${(planned.length)})${description ? `: ${description}` : ''}`,
          ...row,
          ...row.tags,
        };

        delete flattenRow.metadata;
        delete flattenRow.created;
        delete flattenRow.tags;
        delete flattenRow.msg;
        return flattenRow;
      });

      const flattenPlannedByTable = flattenPlanned.map(
        row => Object.keys(row),
      ).reduce((acc, current) => {
        const x = acc.find(item => isEqual(item, current));
        if (!x) {
          return acc.concat([current]);
        }
        return acc;
      }, []).map(tableKeys => flattenPlanned.filter(
        row => isEqual(Object.keys(row), tableKeys),
      ));

      return {
        sheet: capitalize(plannedUpdatedAssetUpdateKey),
        content: flattenPlannedByTable,
      };
    });
  },
);

export const getStatusUpdateToolExcelData = createSelector(
  [
    map.plannedUpdatedStatus,
    map.timezone,
    map.statusFileName,
    map.statusImportTime,
    map.tenant,
    getProjectName,
    getUserInfo,
  ],
  (
    plannedUpdatedStatus,
    timezone,
    fileName,
    importTime,
    tenant,
    projectName,
    userInfo,
  ) => {
    if (!plannedUpdatedStatus) return undefined;

    const { updates, logging } = plannedUpdatedStatus;
    const updateKeys = updates ? Object.keys(updates) : [];
    const keys = [...updateKeys, 'errors', 'warnings', 'info', 'metadata'];
    const data = { ...updates, ...logging };

    const dateFormat = client.cache.current.bootstrap?.project?.calendar.dateFormat;

    return keys.map((plannedUpdatedStatusKey) => {
      const planned = data[plannedUpdatedStatusKey]?.map((row) => {
        let flattenRow = { ...row };
        if (row.tags) {
          flattenRow = {
            ...row,
            ...row.tags,
          };
          delete flattenRow.tags;
        }
        if (row?.msg) {
          flattenRow = {
            info: flattenRow.msg,
            ...flattenRow,
          };
          delete flattenRow.msg;
        }
        delete flattenRow.metadata;

        Object.keys(flattenRow).forEach(
          (key) => {
            flattenRow[key] = isIsoDate(flattenRow[key])
              ? displayDateInProjectTime(flattenRow[key], dateFormat) : flattenRow[key];
          },
        );
        return flattenRow;
      }) || [];

      if (plannedUpdatedStatusKey === 'metadata') {
        return getMetadata({
          tenant,
          projectName,
          userInfo,
          timezone,
          fileName,
          importTime,
        });
      }

      let sheet = '';

      if (plannedUpdatedStatusKey === 'created') {
        sheet = 'Create';
      } else if (plannedUpdatedStatusKey === 'updated') {
        sheet = 'Update';
      } else if (plannedUpdatedStatusKey === 'info') {
        sheet = 'Info';
      } else {
        sheet = capitalize(plannedUpdatedStatusKey);
      }

      return {
        sheet,
        columns: Object.keys(planned[0] || []).map(key => ({
          label: capitalize(key),
          value: key,
        })),
        content: planned,
      };
    });
  },
);

export const getPlannedTwinIDUpdateToolExcelData = createSelector(
  [
    map.plannedUpdatedPlannedTwinID,
    map.timezone,
    map.plannedTwinIDFileName,
    map.plannedTwinIDImportTime,
    map.tenant,
    getAssetStatusesKeyedById,
    getProjectName,
    getUserInfo,
  ],
  (
    plannedUpdatedPlannedTwinID,
    timezone,
    fileName,
    importTime,
    tenant,
    statusesKeyedById,
    projectName,
    userInfo,
  ) => {
    if (!plannedUpdatedPlannedTwinID) return undefined;
    const output: { sheet: string; content: any[] }[] = [];

    const {
      updates, logging, totalCount, unchanged,
    } = plannedUpdatedPlannedTwinID;
    const updateKeys = [
      'created',
      'deleted',
      'byTwinID',
    ];

    updateKeys.forEach((updateKey) => {
      output.push({
        sheet: capitalize(updateKey),
        content: updates[updateKey]
          .map(row => plannedTwinIDToHumanReadableRecord(row, statusesKeyedById)),
      });
    });

    const logKeys = [
      'errors',
      'warnings',
      'info',
    ];

    logKeys.forEach((logKey) => {
      const logs = (logging[logKey] ?? []) as PlanResponseLog[];
      output.push({
        sheet: capitalize(logKey),
        content: logs.map((row) => {
          const { msg, metadata } = row || {};
          const { description } = metadata || {};
          return {
            Log:
              `${msg} (${(logs.length)})${description ? `: ${description}` : ''}`,
            ...row,
            ...row.tags,
          };
        }),
      });
    });

    output.push(
      getMetadata({
        tenant,
        projectName,
        userInfo,
        timezone,
        fileName,
        unchanged,
        importTime,
        totalCount,
      }),
    );

    return output;
  },
);

export const getLabelsOfAssetChanges = createSelector(
  [getAssetChangesKeyedByLabel],
  assetChangesKeyedByLabel => [...assetChangesKeyedByLabel.keys()],
);

export const getAssetTableByLabelKeyedByChanges = createSelector(
  [getAssetChangesKeyedByLabel, getLabelsOfAssetChanges],
  (assetChangesKeyedByLabel, labels) => {
    const keys = getUniqueKeysFromCollection(assetChangesKeyedByLabel);

    const data = new Map();
    if (assetChangesKeyedByLabel) {
      keys.forEach((key) => { data.set(key, []); });

      const formatted = labels.map((label) => {
        const changes = assetChangesKeyedByLabel.get(label)?.filter(({
          isValueChanged,
        }) => isValueChanged !== 'none');

        const changesMap = changes && new Map(changes.map(change => [change.key, change]));
        return {
          label,
          fieldUpdated: changesMap ? [...changesMap.keys()] : [],
        };
      });

      keys.forEach((key) => {
        formatted.forEach(({ label, fieldUpdated }) => {
          if (fieldUpdated.includes(key)) {
            const currentDataSet = data.get(key);
            data.set(key, [...currentDataSet, label]);
          }
        });
      });
    }
    return data as Map<string, any>;
  },
);

export const getAssetChangesKeyedByTwinID = createSelector(
  [
    map.plannedUpdatedAsset,
    getAssetsKeyedByTwinId,
    map.layersKeyedById,
  ],
  (plannedUpdatedAsset, assetsKeyedByTwinId, layersKeyedById) => {
    const { byTwinID } = plannedUpdatedAsset?.updates ?? {};
    if (!byTwinID) return new Map();
    return getAssetChangesKeyedByKey(byTwinID, 'twinID', assetsKeyedByTwinId, layersKeyedById);
  },
);

export const getUpdatedTwinID = createSelector(
  [getAssetChangesKeyedByTwinID],
  assetChangesKeyedByTwinID => [...assetChangesKeyedByTwinID.keys()],
);

export const getAssetTableByTwinIDKeyedByChanges = createSelector(
  [getAssetChangesKeyedByTwinID, getUpdatedTwinID],
  (assetChangesKeyedByTwinID, twinIDs) => {
    const keys = getUniqueKeysFromCollection(assetChangesKeyedByTwinID);

    const data = new Map();
    if (assetChangesKeyedByTwinID) {
      keys.forEach((key) => { data.set(key, []); });

      const formatted = twinIDs?.map((twinID) => {
        const changes = assetChangesKeyedByTwinID.get(twinID)?.filter(({
          isValueChanged,
        }) => isValueChanged !== 'none');

        const changesMap = changes && new Map(changes.map(change => [change.key, change]));
        return {
          twinID,
          fieldUpdated: changesMap ? [...changesMap.keys()] : [],
        };
      });

      keys.forEach((key) => {
        formatted.forEach(({ twinID, fieldUpdated }) => {
          if (fieldUpdated.includes(key)) {
            const currentDataSet = data.get(key);
            data.set(key, [...currentDataSet, twinID]);
          }
        });
      });
    }
    return data as Map<string, any>;
  },
);

export const getPlannedTwinIDUpdateToolSegmentedPicklistColumns = createSelector(
  [getAssetTypes],
  (assetTypes) => {
    const assetTypesWithPlan = assetTypes
      .filter(at => at.plans?.length);
    const columns: DynamicPicklistColumn[] = [
      {
        id: 'assetTypeId',
        options: assetTypesWithPlan.map(at => ({
          label: at.name,
          value: at.id,
        })),
      },
      {
        id: 'planID',
        options: assetTypesWithPlan
          .map(at => at.plans!.map(plan => ({
            label: plan.label,
            value: plan.id,
            filters: {
              assetTypeId: at.id,
            },
          })))
          .flat(),
      },
    ];

    return columns;
  },
);

export const getPlannedTwinIDUpdateToolSegmentedPicklistDefaultValue = createSelector(
  [getAssetTypes],
  (assetTypes) => {
    const assetTypeWithPlan = assetTypes
      .find(at => at.plans?.length);
    if (!assetTypeWithPlan) return undefined;
    return {
      assetTypeId: assetTypeWithPlan.id,
      planID: assetTypeWithPlan.plans![0].id,
    };
  },
);

export const getExistingPlannedTwinIDsKeyedByTwinID = createSelector(
  [map.existingPlannedTwinIDs],
  (existingPlannedTwinIDs) => {
    if (!existingPlannedTwinIDs) return null;
    const plannedTwinIDsKeyedByTwinID = new Map(
      existingPlannedTwinIDs.map(plannedTwinID => [plannedTwinID.twinID, plannedTwinID]),
    );
    return plannedTwinIDsKeyedByTwinID;
  },
);

export const getPlannedTwinIDChangesKeyedByTwinID = createSelector(
  [
    map.plannedUpdatedPlannedTwinID,
    getExistingPlannedTwinIDsKeyedByTwinID,
    getAssetStatusesKeyedById,
  ],
  (plannedUpdatedPlannedTwinID, existingPlannedTwinIDsKeyedByTwinID, assetStatusesKeyedById) => {
    const { byTwinID } = plannedUpdatedPlannedTwinID?.updates ?? {};
    if (!byTwinID || !existingPlannedTwinIDsKeyedByTwinID) return new Map();
    const updateMap = new Map<string, ReturnType<typeof getPlannedTwinIDChanges>>();
    byTwinID?.forEach((plannedTwinID) => {
      const { twinID } = plannedTwinID;
      const currentPlannedTwinID = existingPlannedTwinIDsKeyedByTwinID.get(twinID);
      updateMap.set(twinID, getPlannedTwinIDChanges(
        currentPlannedTwinID,
        plannedTwinID,
        assetStatusesKeyedById,
      ));
    });
    return updateMap;
  },
);

export const getTwinIDsOfPlannedTwinIDsChanges = createSelector(
  [getPlannedTwinIDChangesKeyedByTwinID],
  m => [...m.keys()],
);

export const getUniqueKeysFromExistingPlannedTwinIDs = createSelector(
  [map.existingPlannedTwinIDs],
  (existingPlannedTwinIDs) => {
    if (!existingPlannedTwinIDs) return [];
    const allKeysInExistingPlannedTwinIDs = existingPlannedTwinIDs
      .map(plannedTwinID => Object.keys(plannedTwinID))
      .flat();
    return [...new Set(allKeysInExistingPlannedTwinIDs)];
  },
);

export const getUniqueKeysForPlannedTwinIDUpdate = createSelector(
  [getUniqueKeysFromExistingPlannedTwinIDs, getPlannedTwinIDChangesKeyedByTwinID],
  (uniqueKeysFromExistingPlannedTwinIDs, plannedTwinIDChangesKeyedByTwinID) => {
    if (!plannedTwinIDChangesKeyedByTwinID) return [];
    const newUniqueKeys = getUniqueKeysFromCollection(plannedTwinIDChangesKeyedByTwinID);
    const keys = [...new Set([...uniqueKeysFromExistingPlannedTwinIDs, ...newUniqueKeys])];
    return keys;
  },
);

export const getPlannedTwinIDTableByTwinIDKeyedByChanges = createSelector(
  [
    getUniqueKeysForPlannedTwinIDUpdate,
    getPlannedTwinIDChangesKeyedByTwinID,
    getTwinIDsOfPlannedTwinIDsChanges,
  ],
  (
    uniqueKeysForPlannedTwinIDUpdate,
    plannedTwinIDChangesKeyedByTwinID,
    twinIDsOfPlannedTwinIDsChanges,
  ) => {
    if (!plannedTwinIDChangesKeyedByTwinID) return new Map();
    const keys = uniqueKeysForPlannedTwinIDUpdate;
    const data = new Map<string, string[]>(keys.map(key => [key, []]));

    const formatted = twinIDsOfPlannedTwinIDsChanges.map((twinID) => {
      const changes = plannedTwinIDChangesKeyedByTwinID.get(twinID)?.filter(({
        isValueChanged,
      }) => isValueChanged !== 'none');

      const changesMap = changes && new Map(changes.map(change => [change.key, change]));
      return {
        twinID,
        fieldUpdated: changesMap ? [...changesMap.keys()] : [],
      };
    });

    keys.forEach((key) => {
      formatted.forEach(({ twinID, fieldUpdated }) => {
        if (fieldUpdated.includes(key)) {
          const currentDataSet = data.get(key)!;
          data.set(key, [...currentDataSet, twinID]);
        }
      });
    });
    return data as Map<string, any>;
  },
);

export const getHumanReadablePlannedPlannedTwinIDs = createSelector(
  [
    map.plannedUpdatedPlannedTwinID,
    getAssetStatusesKeyedById,
  ],
  (
    plannedUpdatedPlannedTwinID,
    assetStatusesKeyedById,
  ) => plannedUpdatedPlannedTwinID?.updates.byTwinID.map(
    plannedTwinID => plannedTwinIDToHumanReadableRecord(plannedTwinID, assetStatusesKeyedById),
  ),
);

export const getHumanReadableExistingPlannedTwinIDs = createSelector(
  [
    map.existingPlannedTwinIDs,
    map.plannedUpdatedPlannedTwinID,
    getAssetStatusesKeyedById,
  ],
  (
    existingPlannedTwinIDs,
    plannedUpdatedPlannedTwinID,
    assetStatusesKeyedById,
  ) => {
    const existingPlannedTwinIDsKeyedByTwinID = new Map(
      existingPlannedTwinIDs?.map(plannedTwinID => [plannedTwinID.twinID, plannedTwinID]),
    );
    // For the purpose of the comparison table, only process (computationally heavy)
    // and return existing planned twin IDs that we know show up for the diff.
    return plannedUpdatedPlannedTwinID?.updates.byTwinID.map(
      plannedTwinID => plannedTwinIDToHumanReadableRecord(
        existingPlannedTwinIDsKeyedByTwinID.get(plannedTwinID.twinID),
        assetStatusesKeyedById,
      ),
    );
  },
);

export const getPlannedTwinIDFilterOptions = createSelector(
  [
    getHumanReadablePlannedPlannedTwinIDs,
    getHumanReadableExistingPlannedTwinIDs,
  ],
  (
    humanReadablePlannedTwinIDs,
    humanReadableExistingPlannedTwinIDs,
  ) => {
    if (humanReadablePlannedTwinIDs) {
      const allKeys = Object.values({
        ...humanReadablePlannedTwinIDs,
        ...humanReadableExistingPlannedTwinIDs,
      })
        .map(vals => Object.keys(vals))
        .flat();
      return [...new Set(allKeys)].filter(key => key !== 'twinID');
    }
    return [];
  },
);

export const getAssetFilterOptions = createSelector(
  [
    map.plannedUpdatedAsset,
    map.layersKeyedById,
  ],
  (
    plannedUpdatedAsset,
    layersKeyedById,
  ) => {
    const { byTwinID, byLabel } = plannedUpdatedAsset?.updates || {};
    const getAllUniqueKeys = (arr: AssetUpdate[] | undefined) => {
      if (!arr) return [];
      const humanReadable = arr.map(
        assetUpdate => assetUpdateToHumanReadableRecord(assetUpdate, layersKeyedById),
      );
      return [
        ...new Set(
          humanReadable
            .map(item => Object.keys(item))
            .flat(),
        ),
      ];
    };
    return {
      byLabel: getAllUniqueKeys(byLabel).filter(key => key !== 'label'),
      byTwinID: getAllUniqueKeys(byTwinID).filter(key => key !== 'twinID'),
    };
  },
);
